import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toastError, toastInfo, toastSuccess } from '@src/utils/toast'
import type { State } from '@src/redux/configureStore'
import {
  reportTypeTitles,
  unknownTypeTitle
} from '@src/modules/reports/v2/helpers/grid.columns'
import { defaultHandleStatus, getErrorResponse } from '@src/endpoints/helpers'
import {
  ReportDetail,
  UpdateReportRequest,
  listReports,
  deleteReports,
  downloadReport,
  updateReport,
  ListReportsRequest
} from '@src/endpoints/openapis/saas-cam-reportmanager'

import { isEmpty } from 'lodash'

export const initialState = {
  isPending: false,
  reportsInfo: [] as ReportDetail[],
  currentOffset: 0,
  hasNext: true,
  hasPrev: false,
  totalCount: 0
}

interface GetDataPayload extends ReportDetail {
  isPending?: boolean
}

const reports = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    changeState: (
      state,
      action: PayloadAction<Partial<typeof initialState>>
    ) => ({ ...state, ...action.payload }),
    pending: (state, action: PayloadAction<{ isPending?: boolean }>) => {
      state.isPending = action.payload.isPending
    },
    loadedData: (state, action: PayloadAction<GetDataPayload>) => ({
      ...state,
      ...action.payload
    }),
    dispose: state => initialState
  }
})

export const onRemoveReport =
  (params: {
    reportId: string
    reportName: string
    reqParams: Partial<ListReportsRequest>
  }) =>
  (dispatch: any, getStore: () => State) => {
    deleteReports({
      reportIds: [params.reportId]
    })
      .then(res => {
        const errors = getErrorResponse(res)
        if (isEmpty(errors)) {
          toastSuccess(`Report ${params?.reportName} has been removed`)
          dispatch(fetchData(getFetchParams(params.reqParams)))
        } else {
          toastError(`Unable to remove report!\n${errors}`)
        }
      })
      .catch(ex => {
        toastError(`Unable to remove report ${params?.reportName}!`)
      })
  }

export function onDownloadReport(params: {
  reportId: string
  reportName: string
  reqParams: Partial<ListReportsRequest>
}) {
  return (dispatch: any, getStore: () => State) => {
    toastInfo(`Downloading report ${params?.reportName}...`)
    downloadReport({ reportIds: [params.reportId] })
      .then(res => defaultHandleStatus(res, null, true))
      .then(res => {
        const errors = getErrorResponse(res)
        if (isEmpty(errors)) {
          const a = window.document.createElement('a')
          a.href = window.URL.createObjectURL(res?.file)
          a.setAttribute('download', res?.filename || params.reportName)
          a.click()
          dispatch(fetchData(getFetchParams(params.reqParams)))
        } else {
          toastError(`Unable to download reports!\n${errors}`)
        }
      })
      .catch(ex => {
        toastError(`Failed to download report!`)
        return ex
      })
  }
}

export const onDownloadMultipleReports =
  (reportIds: string[], reqParams: Partial<ListReportsRequest>) =>
  (dispatch: any, getStore: () => State) => {
    toastInfo(`Downloading ${reportIds.length} reports...`)
    downloadReport({ reportIds })
      .then(res => defaultHandleStatus(res, null, true))
      .then(res => {
        const errors = getErrorResponse(res)
        if (isEmpty(errors)) {
          const a = window.document.createElement('a')
          a.href = window.URL.createObjectURL(res?.file)
          a.setAttribute('download', res?.filename || 'reports.zip')
          a.click()
          dispatch(fetchData(getFetchParams(reqParams)))
        } else {
          toastError(`Unable to download reports!\n${errors}`)
        }
      })
      .catch(ex => {
        toastError(`Failed to download reports!`)
        return ex
      })
  }

export const onRemoveMultipleReports =
  (items: string[], reqParams: Partial<ListReportsRequest>) =>
  (dispatch: any, getStore: () => State) => {
    deleteReports({
      reportIds: items
    })
      .then(res => {
        const errors = getErrorResponse(res)
        if (isEmpty(errors)) {
          toastSuccess(`${items.length} reports has been removed`)
          dispatch(fetchData(getFetchParams(reqParams)))
        } else {
          toastError(`Unable to remove reports!\n${errors}`)
        }
      })
      .catch(ex => {
        toastError(`Unable to remove ${items.length} reports!`)
      })
  }

export const onUpdateReport =
  (
    params: UpdateReportRequest,
    reqParams: Partial<ListReportsRequest>,
    onSuccess?: () => void,
    onError?: () => void
  ) =>
  (dispatch: any, getStore: () => State) => {
    updateReport({
      ...params
    })
      .then(res => {
        const errors = getErrorResponse(res)
        if (isEmpty(errors)) {
          if (typeof onSuccess === 'function') onSuccess()
          toastSuccess(`Report has been updated`)
          dispatch(
            fetchData({
              offset: '0',
              count: true,
              ...reqParams
            })
          )
        } else {
          toastError(
            `Unable to update ${params?.updateReportReq?.name} report!\n${errors}`
          )
          if (typeof onError === 'function') onError()
        }
      })
      .catch(ex => {
        if (typeof onError === 'function') onError()
        toastError(`Unable to update ${params?.updateReportReq?.name} report!`)
      })
  }

const getFetchParams = (
  reqParams: Partial<ListReportsRequest>
): Partial<ListReportsRequest> => ({
  count: true,
  offset: '0',
  ...reqParams
})

export const fetchData =
  (params: ListReportsRequest = {} as ListReportsRequest) =>
  (dispatch: any, getStore: () => State) => {
    dispatch(
      reports.actions.pending({
        isPending: true
      })
    )
    listReports({ ...params })
      .then(res => {
        const errors = getErrorResponse(res)
        if (isEmpty(errors)) {
          dispatch(
            reports.actions.loadedData({
              reportsInfo: (res?.entries || []).map(r => ({
                ...r,
                type: reportTypeTitles[r.type] || unknownTypeTitle
              })),
              isPending: false,
              totalCount: res?.count
            })
          )
        } else {
          toastError(`Failed to get reports list\nError: ${errors}`)
          dispatch(
            reports.actions.pending({
              isPending: false
            })
          )
        }
      })
      .catch(ex => {
        toastError('Failed to get reports list')
        dispatch(
          reports.actions.pending({
            isPending: false
          })
        )
        return ex
      })
  }

export const { reducer, actions, caseReducers } = reports
export default reports
